import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, tap, interval, Observable, startWith, switchMap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IServiceOrder } from '../serviceorder/models/IServiceOrder';
import { IPrioSoOrderAlert } from './models/IPrioSoOrderAlert';

@Injectable({
  providedIn: 'root'
})
export class PrioServiceorderService {
  private env = environment;
  private prioOrdersAlertSubject = new BehaviorSubject<IPrioSoOrderAlert[]>([]);
  public prioOrdersAlert$ = this.prioOrdersAlertSubject.asObservable();
  private broadcastChannel = new BroadcastChannel('prio-order-alerts');
  private broadcastChannelClosed = false;

  http = inject(HttpClient);

  constructor() {
   this.broadcastChannel = new BroadcastChannel('prio-order-alerts');
    this.setupBroadcastChannelHandler();

    // Load saved alert state from localStorage on service initialization
    const savedAlerts = this.loadAlertState();
    this.prioOrdersAlertSubject.next(savedAlerts.filter(o => !o.handled));
  }

  private setupBroadcastChannelHandler() {
    this.broadcastChannel.onmessage = (event) => {
      const message = event.data;

      let updatedAlerts: IPrioSoOrderAlert[] = this.loadAlertState();

      if (message.type === 'newOrders') {
        this.prioOrdersAlertSubject.next(message.orders);
        this.saveAlertState(message.orders);
      }

      if (message.type === 'markSeen') {
        updatedAlerts = updatedAlerts.map(order => ({ ...order, seen: true }));
        this.saveAlertState(updatedAlerts);
        this.prioOrdersAlertSubject.next(updatedAlerts);
      }

      if (message.type === 'markHandled') {
        const orderId = message.orderId;
        updatedAlerts = updatedAlerts.map(order =>
          order.soId === orderId ? { ...order, handled: true } : order
        );

        this.saveAlertState(updatedAlerts);
        this.prioOrdersAlertSubject.next(updatedAlerts.filter(o => !o.handled));
      }
    };
  }

  private ensureBroadcastChannelOpen() {
    if (!this.broadcastChannel || this.broadcastChannelClosed) {
      this.broadcastChannel = new BroadcastChannel('prio-order-alerts');
      this.setupBroadcastChannelHandler();
      this.broadcastChannelClosed = false;
    }
  }

  private saveAlertState(orders: IPrioSoOrderAlert[]): void {
    localStorage.setItem('prioAlertState', JSON.stringify(orders));
  }

  private loadAlertState(): IPrioSoOrderAlert[] {
    const stored = localStorage.getItem('prioAlertState');
    return stored ? JSON.parse(stored) : [];
  }

  markAsHandled(orderId: number): void {
    const updated = this.prioOrdersAlertSubject.value.map(order =>
      order.soId === orderId ? { ...order, handled: true } : order
    );

    this.saveAlertState(updated);
    this.prioOrdersAlertSubject.next(updated);

    this.broadcastChannel.postMessage({
      type: 'markHandled',
      orderId
    });
  }

  markAllAsSeen(): void {
    const updated = this.prioOrdersAlertSubject.value.map(order => ({
      ...order,
      seen: true
    }));

    this.saveAlertState(updated);
    this.prioOrdersAlertSubject.next(updated);

    this.broadcastChannel.postMessage({
      type: 'markSeen'
    });
  }

  getLastCheckTime(): string {
    const stored = localStorage.getItem('lastPrioSoSyncTime');
    return stored && !isNaN(Date.parse(stored)) ? stored : new Date().toISOString();
  }

  setLastCheckTime(time: string): void {
    localStorage.setItem('lastPrioSoSyncTime', time);
  }

  startPolling(): void {
    interval(this.env.prioSoPollInterval).pipe( // Poll every 5 minutes in production. 1 minute in dev
      startWith(0),
      switchMap(() => this.checkNewPrioServiceOrders(this.getLastCheckTime())),
      tap(() => this.setLastCheckTime(new Date().toISOString())),
      map((ordersFromBackend) => {
        const savedState = this.loadAlertState();

        // Filter out serviceorders of type "beställningsuppdrag". Not urgent
        const filteredOrdersFromBackend = ordersFromBackend.filter(so => 
          so.soClass !== this.env.soClassOrderAssignment
        );

        // Map backend serviceorders to include local seen/handled info
        const backendMapped = filteredOrdersFromBackend.map(order => {
          const existing = savedState.find(o => o.soId === order.soId);
          const wasModified = !existing || new Date(order.modifyDate) > new Date(existing.modifyDate);

          return {
            ...order,
            seen: existing?.handled ? false : existing?.seen ?? false,
            handled: wasModified ? false : existing?.handled ?? false,
          };
        });

        // Keep existing unhandled alerts
        const stillRelevantLocal = savedState.filter(local =>
          !local.handled &&
          !backendMapped.find(o => o.soId === local.soId)
        );

        const finalOrders = [...backendMapped, ...stillRelevantLocal];

        // Save merged state to localStorage
        this.saveAlertState(finalOrders);

        return finalOrders.filter(o => !o.handled);
      })
    ).subscribe(newOrders => {
      
      this.prioOrdersAlertSubject.next(newOrders);

      this.ensureBroadcastChannelOpen();
      try { // This sends data to all tabs if the user has multiple tabs open
        this.broadcastChannel.postMessage({
          type: 'newOrders',
          orders: newOrders
        });
      } catch (error) {
        console.error('Failed to post message on BroadcastChannel:', error);
      }
    });
  }

  clearAlerts(): void {
    this.prioOrdersAlertSubject.next([]);
    localStorage.removeItem('prioAlertState');
    localStorage.removeItem('lastPrioSoSyncTime');
    this.broadcastChannelClosed = true;
    this.broadcastChannel.close(); // closes the channel
  }

  checkNewPrioServiceOrders(since: string): Observable<IPrioSoOrderAlert[]> {
    return this.http.get<IPrioSoOrderAlert[]>(`${this.env.apiUrl}/PrioServiceOrder/CheckNewPrioServiceorders`, { params: { since } });
  }

  getPrioServiceOrders(): Observable<any> {
    return this.http.get<any>(`${this.env.apiUrl}/PrioServiceOrder/GetPrioServiceorders`);
  }

  handlePrioServiceOrder(serviceOrderId: number, comment: string): Observable<any> {
    let requestBody = {
      soId: serviceOrderId,
      comment: comment
    };
    return this.http.post<any>(`${this.env.apiUrl}/PrioServiceOrder/HandlePrioServiceOrder`, requestBody);
  }
}